import { Box, Button, Container, Heading, useVideo } from '@dapperlabs/core-fe';
import { useEffect, useRef } from 'react';
import { useIsClient } from 'src/general/utils/useIsClient';

interface Props {
  buttonLabel: string;
  secondaryVideoContentUrl: string;
  sectionHeading: string;
  trackEventName?: string;
  trackHandler?: ({
    item_label,
    event_name,
  }: {
    event_name: string;
    item_label: string;
  }) => void;
  trackItemLabel?: string;
  videoContent: string;
}

export const AboutHero = ({
  sectionHeading,
  buttonLabel,
  videoContent,
  secondaryVideoContentUrl,
  trackHandler,
  trackItemLabel,
  trackEventName,
}: Props) => {
  const isClient = useIsClient();
  const enterFullScreenRef = useRef<(() => void) | null>(null);

  return (
    <Container
      as='header'
      position='relative'
      justifyContent='center'
      maxWidth='90rem'
      h='40rem'
      px={0}
      backgroundColor='black'
      centerContent
    >
      <Box pos='absolute' zIndex={1} textAlign='center'>
        <Heading
          as='h1'
          mb={[16, 16, 6]}
          px={['1rem', '1rem', '1rem', '4rem']}
          fontFamily='heading'
          fontSize={['2.2rem', '2.2rem', '2.2rem', '4.375rem']}
          fontWeight='500'
          lineHeight={1}
          letterSpacing='-1px'
        >
          {sectionHeading}
        </Heading>
        <Button
          px={[12, 12, 36]}
          textTransform='capitalize'
          onClick={() => {
            if (enterFullScreenRef.current) {
              enterFullScreenRef.current();
            }
            if (trackHandler && trackItemLabel) {
              trackHandler({
                event_name: trackEventName ?? '',
                item_label: trackItemLabel,
              });
            }
          }}
          variant='outline'
        >
          {buttonLabel}
        </Button>
      </Box>
      {isClient && (
        <AboutHeroVideo
          onEnterFullScreenReady={(fn) => {
            enterFullScreenRef.current = fn;
          }}
          secondaryVideoContentUrl={secondaryVideoContentUrl}
          videoContent={videoContent}
        />
      )}
    </Container>
  );
};

const AboutHeroVideo = ({
  secondaryVideoContentUrl,
  videoContent,
  onEnterFullScreenReady,
}: {
  onEnterFullScreenReady: (fn: () => void) => void;
  secondaryVideoContentUrl: string;
  videoContent: string;
}) => {
  const { enterFullScreen, view } = useVideo({
    fullScreenVideoSrc: secondaryVideoContentUrl,
    id: 'about-video',
    previewSrc: '',
    sx: {
      height: '100%',
      objectFit: 'cover',
    },
    videoProps: {
      display: 'block',
      h: '100%',
      loop: true,
      objectFit: 'cover',
      opacity: '0.6',
      w: '100%',
    },
    videoSrc: videoContent,
  });

  useEffect(() => {
    onEnterFullScreenReady(enterFullScreen);
  }, [enterFullScreen, onEnterFullScreenReady]);

  return <>{view}</>;
};
